/**
 * Receiver page — camera preview, parallel QR decoding, progress and result.
 */
import { QrReceiver, ensureQwertyWasm } from '@qwerty/engine/qwerty';
import { QrDecodePool, type Decoder } from './decode_pool';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Wait until the camera stream reports its real frame size. */
function waitForVideoMetadata(video: HTMLVideoElement): Promise<void> {
  if (video.videoWidth > 0 && video.videoHeight > 0) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      video.removeEventListener('loadedmetadata', done);
      resolve();
    };
    video.addEventListener('loadedmetadata', done);
    window.setTimeout(done, 2000);
  });
}

export function createReceiverPage(setStatus: (text: string) => void): HTMLElement {
  const ui = window.__SKF_UI;

  const field = (label: string, control: HTMLElement): HTMLElement => {
    const wrap = ui.el('div', 'qwerty-field');
    wrap.appendChild(ui.el('span', 'skf-caption-heading', label));
    wrap.appendChild(control);
    return wrap;
  };

  const root = ui.el('div', 'qwerty-page-root skf-container skf-container--fluid');
  const row = ui.el('div', 'skf-row skf-g-3');
  root.appendChild(row);

  const cameraCol = ui.el('div', 'skf-col-12 skf-col-lg-7');
  const resultCol = ui.el('div', 'skf-col-12 skf-col-lg-5');
  row.append(cameraCol, resultCol);

  // ── camera card ─────────────────────────────────────────────────────────────
  const cameraCard = ui.el('section', 'skf-card qwerty-card');
  const head = ui.el('div', 'qwerty-status-row');
  head.appendChild(ui.el('h2', 'skf-title-4', '摄像头扫描'));
  const badge = ui.el('span', 'skf-tag', '未开始');
  head.appendChild(badge);
  cameraCard.appendChild(head);

  const videoWrap = ui.el('div', 'qwerty-video-wrap');
  const video = document.createElement('video');
  video.playsInline = true;
  video.muted = true;
  const preview = document.createElement('canvas');
  preview.width = 960;
  preview.height = 540;
  videoWrap.append(video, preview);
  cameraCard.appendChild(videoWrap);
  const streamInfo = ui.el('div', 'skf-caption skf-label-dim', '摄像头：未启动');
  cameraCard.appendChild(streamInfo);

  let decoder: Decoder = 'zxing';
  const decoderRow = ui.el('div', 'qwerty-seg-row');
  ui.segmented(decoderRow, [
    { id: 'zxing', label: 'ZXing', title: 'ZXing-C++ WASM — 识别更稳、速度更快（默认）' },
    { id: 'rqrr', label: 'rqrr', title: '纯 Rust 轻量解码器 — WASM 约为 ZXing 的 1/8，但每码约 13ms' },
  ], decoder, (id) => { decoder = id === 'rqrr' ? 'rqrr' : 'zxing'; });
  cameraCard.appendChild(field('解码器', decoderRow));

  let workerCount = 2;
  const workerRow = ui.el('div', 'qwerty-seg-row');
  ui.segmented(workerRow, [1, 2, 3, 4].map((n) => ({ id: String(n), label: String(n) + ' 路' })), String(workerCount), (id) => {
    workerCount = Number(id);
    if (scanning) rebuildPool();
  });
  cameraCard.appendChild(field('解码并行度', workerRow));

  let scanFps = 12;
  const fpsValue = ui.el('span', 'qwerty-value', scanFps + ' fps');
  const fpsInput = document.createElement('input');
  fpsInput.type = 'range';
  fpsInput.className = 'skf-slider';
  fpsInput.min = '2';
  fpsInput.max = '60';
  fpsInput.step = '1';
  fpsInput.value = String(scanFps);
  fpsInput.addEventListener('input', () => {
    scanFps = Number(fpsInput.value);
    fpsValue.textContent = scanFps + ' fps';
  });
  const fpsRow = ui.el('div');
  fpsRow.append(fpsInput, fpsValue);
  cameraCard.appendChild(field('扫描帧率', fpsRow));

  const actions = ui.el('div', 'qwerty-actions');
  const scanBtn = document.createElement('button');
  scanBtn.className = 'skf-btn skf-btn-suggested';
  scanBtn.textContent = '开始扫描';
  actions.appendChild(scanBtn);
  const resetBtn = document.createElement('button');
  resetBtn.className = 'skf-btn skf-btn-flat';
  resetBtn.textContent = '重置';
  resetBtn.addEventListener('click', () => {
    receiver.reset();
    clearResult();
    renderProgress();
  });
  actions.appendChild(resetBtn);
  cameraCard.appendChild(actions);
  cameraCol.appendChild(cameraCard);

  // ── result card ─────────────────────────────────────────────────────────────
  const resultCard = ui.el('section', 'skf-card qwerty-card');
  resultCard.appendChild(ui.el('h2', 'skf-title-4', '接收状态'));

  const statusRow = ui.el('div', 'qwerty-status-row');
  const progress = ui.el('div', 'skf-progress');
  const progressBar = ui.el('div', 'skf-progress-bar');
  progressBar.style.width = '0%';
  progress.appendChild(progressBar);
  const percent = ui.el('span', 'skf-numeric', '0%');
  statusRow.append(progress, percent);
  resultCard.appendChild(statusRow);

  const statsGrid = ui.el('div', 'qwerty-stats');
  const statCard = (label: string): { root: HTMLElement; value: HTMLElement } => {
    const box = ui.el('div', 'qwerty-stat');
    box.appendChild(ui.el('span', 'skf-caption skf-label-dim', label));
    const value = ui.el('b', '', '—');
    box.appendChild(value);
    return { root: box, value };
  };
  const statAccepted = statCard('已收符号');
  const statDupes = statCard('重复');
  const statNeeded = statCard('需要');
  const statSize = statCard('载荷大小');
  statsGrid.append(statAccepted.root, statDupes.root, statNeeded.root, statSize.root);
  resultCard.appendChild(statsGrid);

  const resultBox = ui.el('div', 'qwerty-hidden');
  const resultText = document.createElement('textarea');
  resultText.className = 'skf-textarea qwerty-result-text';
  resultText.readOnly = true;
  resultBox.appendChild(resultText);
  resultCard.appendChild(resultBox);

  const downloadRow = ui.el('div', 'qwerty-actions qwerty-hidden');
  const downloadBtn = document.createElement('button');
  downloadBtn.className = 'skf-btn';
  downloadBtn.textContent = '下载文件';
  downloadRow.appendChild(downloadBtn);
  resultCard.appendChild(downloadRow);

  const errorAlert = ui.el('div', 'skf-alert skf-alert--danger qwerty-hidden');
  resultCard.appendChild(errorAlert);
  resultCol.appendChild(resultCard);

  // ── state ───────────────────────────────────────────────────────────────────
  const receiver = new QrReceiver();
  let pool: QrDecodePool | null = null;
  let scanning = false;
  let scanTimer: number | null = null;
  let stream: MediaStream | null = null;
  let inFlight = false;
  let rotate = 0;
  let lastResult: { isText: boolean; data?: ArrayBuffer; filename?: string; mime?: string } | null = null;

  const renderProgress = (): void => {
    const p = receiver.progress;
    const ratio = p.needed > 0 ? Math.min(1, p.accepted / p.needed) : 0;
    progressBar.style.width = (ratio * 100).toFixed(1) + '%';
    percent.textContent = Math.round(ratio * 100) + '%';
    statAccepted.value.textContent = String(p.accepted);
    statDupes.value.textContent = String(p.duplicates);
    statNeeded.value.textContent = p.needed > 0 ? String(p.needed) : '—';
    statSize.value.textContent = p.dataLength > 0 ? p.dataLength + ' B' : '—';
  };

  const clearResult = (): void => {
    resultBox.classList.add('qwerty-hidden');
    downloadRow.classList.add('qwerty-hidden');
    errorAlert.classList.add('qwerty-hidden');
    lastResult = null;
    progressBar.style.width = '0%';
    percent.textContent = '0%';
    statAccepted.value.textContent = '0';
    statDupes.value.textContent = '0';
    statNeeded.value.textContent = '—';
    statSize.value.textContent = '—';
  };

  const showResult = (): void => {
    const result = receiver.currentResult;
    if (!result) return;
    const p = receiver.progress;
    badge.textContent = '已完成';
    badge.className = 'skf-tag skf-tag--success';
    progressBar.style.width = '100%';
    percent.textContent = '100%';
    errorAlert.classList.add('qwerty-hidden');
    setStatus('接收完成 · 传输 #' + p.transferId.toString(16));

    if (result.isText) {
      lastResult = { isText: true };
      resultText.value = result.text;
    } else {
      const buffer = result.data.buffer.slice(result.data.byteOffset, result.data.byteOffset + result.data.byteLength) as ArrayBuffer;
      lastResult = { isText: false, data: buffer, filename: result.filename, mime: result.mime };
      resultText.value = result.filename + ' · ' + result.data.byteLength + ' B · ' + result.mime;
    }
    resultBox.classList.remove('qwerty-hidden');
    downloadRow.classList.toggle('qwerty-hidden', result.isText);
    if (scanning) stopScan();
  };

  const consume = (symbols: Uint8Array[]): void => {
    if (receiver.currentResult) return;
    for (const bytes of symbols) {
      receiver.push(bytes);
      if (receiver.currentResult) break;
    }
    if (receiver.currentResult) showResult();
    else {
      renderProgress();
      if (receiver.progress.phase === 'receiving') {
        badge.textContent = '接收中';
        badge.className = 'skf-tag skf-tag--warning';
      }
    }
  };

  const rebuildPool = (): void => {
    if (pool) pool.terminate();
    pool = new QrDecodePool(workerCount);
  };

  const scanTick = (): void => {
    if (!scanning) return;
    if (!inFlight && pool && video.readyState >= 2) {
      const ctx = preview.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        if (rotate) {
          ctx.save();
          ctx.translate(preview.width, 0);
          ctx.rotate(Math.PI / 2);
          ctx.drawImage(video, 0, 0, preview.height, preview.width);
          ctx.restore();
        } else {
          ctx.drawImage(video, 0, 0, preview.width, preview.height);
        }
        const image = ctx.getImageData(0, 0, preview.width, preview.height);
        inFlight = true;
        pool
          .decode(image, decoder, 4)
          .then(consume)
          .catch((err) => {
            errorAlert.textContent = messageOf(err);
            errorAlert.classList.remove('qwerty-hidden');
          })
          .finally(() => { inFlight = false; });
      }
    }
    scanTimer = window.setTimeout(scanTick, Math.max(1, Math.round(1000 / scanFps)));
  };

  const startScan = async (): Promise<void> => {
    if (scanning) return;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      video.srcObject = stream;
      await video.play();
      await waitForVideoMetadata(video);
      // Match the capture canvas to the camera's real aspect ratio. A fixed
      // 16:9 canvas stretched iPhone frames and distorted the QR squares.
      const vw = video.videoWidth || 1280;
      const vh = video.videoHeight || 720;
      const factor = Math.min(1, 1280 / Math.max(vw, vh));
      const sw = Math.max(1, Math.round(vw * factor));
      const sh = Math.max(1, Math.round(vh * factor));
      // iOS often hands back a landscape frame while the phone is held in
      // portrait. Rotate it 90° so the viewfinder (and the QR) is upright.
      const viewportPortrait = window.innerHeight >= window.innerWidth;
      rotate = viewportPortrait && vw > vh ? 90 : 0;
      preview.width = rotate ? sh : sw;
      preview.height = rotate ? sw : sh;
      // Match the viewport to the stream so it never stretches or over-talls.
      videoWrap.style.aspectRatio = preview.width + ' / ' + preview.height;
      streamInfo.textContent =
        '摄像头 ' + vw + '×' + vh +
        ' · 旋转 ' + rotate + '°' +
        ' · 画布 ' + preview.width + '×' + preview.height +
        ' · 视口 ' + window.innerWidth + '×' + window.innerHeight +
        ' · DPR ' + window.devicePixelRatio;
    } catch (err) {
      errorAlert.textContent = '无法访问摄像头：' + messageOf(err);
      errorAlert.classList.remove('qwerty-hidden');
      setStatus('摄像头不可用');
      return;
    }
    try {
      await ensureQwertyWasm();
    } catch (err) {
      errorAlert.textContent = 'RaptorQ WASM 初始化失败：' + messageOf(err);
      errorAlert.classList.remove('qwerty-hidden');
      setStatus('引擎初始化失败');
      return;
    }
    receiver.reset();
    rebuildPool();
    clearResult();
    scanning = true;
    inFlight = false;
    badge.textContent = '扫描中';
    badge.className = 'skf-tag skf-tag--warning';
    scanBtn.textContent = '停止扫描';
    setStatus('正在扫描二维码…');
    scanTick();
  };

  const stopScan = (): void => {
    scanning = false;
    if (scanTimer !== null) {
      window.clearTimeout(scanTimer);
      scanTimer = null;
    }
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
      stream = null;
    }
    video.srcObject = null;
    if (pool) {
      pool.terminate();
      pool = null;
    }
    scanBtn.textContent = '开始扫描';
    if (badge.textContent === '扫描中') {
      badge.textContent = '已停止';
      badge.className = 'skf-tag';
    }
  };

  downloadBtn.addEventListener('click', () => {
    if (!lastResult || lastResult.isText || !lastResult.data) return;
    const blob = new Blob([lastResult.data], { type: lastResult.mime || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = lastResult.filename || 'qwerty-download';
    anchor.click();
    URL.revokeObjectURL(url);
  });

  scanBtn.addEventListener('click', () => {
    if (scanning) stopScan();
    else void startScan();
  });

  return root;
}
